import {
	Button,
	Checkbox,
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	InputNumber,
	notification,
	Radio,
	Result,
	Row,
	Select,
	Space,
	Spin,
	Upload,
} from "antd";
import React, { useEffect, useRef, useState } from "react";
import Layout from "../layout/Layout";
import SignatureCanvas from "react-signature-canvas";
import { api } from "../utils/Api";
import axios from "axios";
import dayjs from "dayjs";
import {
	businessTypeOptions,
	federalTaxClassificationOptions,
} from "../../constants/Customer";
import { UploadOutlined } from "@ant-design/icons";
import { CustomSpinner } from "../utils/CustomComponents";

const CreditNew4 = (props) => {
	const [form] = Form.useForm();
	const [businessType, setBusinessType] = useState("");
	const [acceptConditions, setAcceptConditions] = useState(false);
	const [loading, setLoading] = useState(true);
	const [isLoading, setIsLoading] = useState(true);
	const [creditMade, setCreditMade] = useState(true);
	const signaturePadRef = useRef(null);
	const signatureFirstGuarantorPadRef = useRef(null);
	const signatureSecondGuarantorPadRef = useRef(null);
	const [apiNotification, contextHolder] = notification.useNotification();

	const [otpVerified, setOtpVerified] = useState(null);

	const [otpCode, setOtpCode] = useState("");

	const [verifyingOtp, setVerifyingOtp] = useState(false);

	const openNotification = (type, message, description) => {
		apiNotification[type]({
			message: message,
			description: description,
		});
	};

	useEffect(() => {
		checkAccess();
	}, []);

	const getToken = () => {
		return localStorage.getItem(`credit_access_${props.match.params.id}`);
	};

	const removeAccess = () => {
		localStorage.removeItem(`credit_access_${props.match.params.id}`);

		setOtpVerified(false);
	};

	const getCreditHeaders = () => {
		const token = getToken();

		return {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		};
	};

	const checkAccess = async () => {
		try {
			const token = getToken();

			if (!token) {
				setOtpVerified(false);
				return;
			}

			const url =
				api + `/customer/check/credit-access/${props.match.params.id}`;

			const { data } = await axios.get(url, getCreditHeaders());

			if (data.success) {
				setOtpVerified(true);

				await validationOnUrl();
			} else {
				removeAccess();
			}
		} catch (err) {
			console.error(err);

			removeAccess();
		} finally {
			setIsLoading(false);
		}
	};

	const verifyOTP = async (code = otpCode) => {
		try {
			if (verifyingOtp) {
				return;
			}

			if (!code || code.length !== 6) {
				openNotification(
					"warning",
					"Invalid Code",
					"Please enter the 6 digit code",
				);
				return;
			}

			setVerifyingOtp(true);

			const url = api + "/customer/check/verify-otp";

			const { data } = await axios.post(url, {
				customerId: props.match.params.id,
				otpCode: code,
			});

			if (data.success) {
				localStorage.setItem(
					`credit_access_${props.match.params.id}`,
					data.payload,
				);

				setOtpVerified(true);

				openNotification("success", "Success", "OTP verified successfully");

				await validationOnUrl();
			} else {
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			console.error(err);

			openNotification("error", "Error", "Error verifying OTP");
		} finally {
			setVerifyingOtp(false);
		}
	};

	const OTPScreen = () => {
		return (
			<div
				style={{
					maxWidth: 400,
					margin: "0 auto",
					paddingTop: 100,
					textAlign: "center",
				}}
			>
				<Result
					status="info"
					title="OTP Verification"
					subTitle="Enter the verification code sent to your phone or email"
				/>

				<Input.OTP
					size="large"
					length={6}
					value={otpCode}
					inputMode="numeric"
					disabled={verifyingOtp}
					onChange={(value) => {
						const formatted = value.replace(/\D/g, "");

						setOtpCode(formatted);

						if (formatted.length === 6 && !verifyingOtp) {
							verifyOTP(formatted);
						}
					}}
				/>

				<Button
					type="primary"
					size="large"
					block
					style={{
						marginTop: 20,
					}}
					onClick={() => verifyOTP()}
					loading={verifyingOtp}
					disabled={verifyingOtp || otpCode.length !== 6}
				>
					Verify Code
				</Button>
			</div>
		);
	};

	const validationOnUrl = async () => {
		try {
			const url = api + "/credit/check/public/" + props.match.params.id;

			const { data } = await axios.get(url, getCreditHeaders());

			if (data.success) {
				if (data.payload) {
					setCreditMade(true);
				} else {
					setCreditMade(false);

					await getCustomer();
				}
			} else {
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			console.error(err);

			if (
				err.response?.status === 401 ||
				err.response?.data?.message === "Unauthorized"
			) {
				removeAccess();
			}

			openNotification("error", "Error", "Error while trying check credit");
		} finally {
			setLoading(false);
			setIsLoading(false);
		}
	};

	const getCustomer = async () => {
		const url = api + "/customer/profile/get/public/" + props.match.params.id;

		try {
			const { data } = await axios.get(url, getCreditHeaders());

			if (data.success) {
				const payload = data.payload;
				form.setFieldsValue({
					id: payload._id,
					name: payload.name,
					dba: payload.dba,
					taxId: payload.taxId,
					federalTaxClassification: payload.federalTaxClassification,
					date: dayjs(),
					state: "TX",
					poRequired: false,
					taxExempt: false,
					invoiceMethod: "Email",
					acceptConditions: false,
					prefix: "+1",
				});
			} else {
				openNotification("error", "Error", data.message);
			}

			setLoading(false);
		} catch (err) {
			console.error(err);

			if (
				err.response?.status === 401 ||
				err.response?.data?.message === "Unauthorized"
			) {
				removeAccess();
			}

			openNotification("error", "Error", "Error while trying to get customer");
		}
	};

	const handleClear = () => {
		signaturePadRef.current.clear();
	};
	const handleFirstGuarantorClear = () => {
		signatureFirstGuarantorPadRef.current.clear();
	};
	const handleSecondGuarantorClear = () => {
		signatureSecondGuarantorPadRef.current.clear();
	};

	const addOwners = (values) => {
		let owners = [];

		if (values.ownerName1) {
			let owner = {
				ownerName: values.ownerName1,
				ownerPosition: values.ownerPosition1,
				ownerOwned: values.ownerOwned1,
				ownerAddress: values.ownerAddress1,
				ownerPhone: values.ownerPhone1 ? `+1${values.ownerPhone1}` : undefined,
			};
			owners.push(owner);
		}

		if (values.ownerName2) {
			let owner = {
				ownerName: values.ownerName2,
				ownerPosition: values.ownerPosition2,
				ownerOwned: values.ownerOwned2,
				ownerAddress: values.ownerAddress2,
				ownerPhone: values.ownerPhone2 ? `+1${values.ownerPhone2}` : undefined,
			};
			owners.push(owner);
		}

		if (values.ownerName3) {
			let owner = {
				ownerName: values.ownerName3,
				ownerPosition: values.ownerPosition3,
				ownerOwned: values.ownerOwned3,
				ownerAddress: values.ownerAddress3,
				ownerPhone: values.ownerPhone3 ? `+1${values.ownerPhone3}` : undefined,
			};
			owners.push(owner);
		}

		return owners;
	};

	const addReferences = (values) => {
		let referencias = [];

		if (values.referenceName1) {
			let refe = {
				referenceName: values.referenceName1,
				referenceAddress: values.referenceAddress1,
				referencePhone: values.referencePhone1
					? `+1${values.referencePhone1}`
					: undefined,
				referenceEmail: values.referenceEmail1,
			};
			referencias.push(refe);
		}

		if (values.referenceName2) {
			let refe = {
				referenceName: values.referenceName2,
				referenceAddress: values.referenceAddress2,
				referencePhone: values.referencePhone2
					? `+1${values.referencePhone2}`
					: undefined,
				referenceEmail: values.referenceEmail2,
			};
			referencias.push(refe);
		}

		if (values.referenceName3) {
			let refe = {
				referenceName: values.referenceName3,
				referenceAddress: values.referenceAddress3,
				referencePhone: values.referencePhone3
					? `+1${values.referencePhone3}`
					: undefined,
				referenceEmail: values.referenceEmail3,
			};
			referencias.push(refe);
		}

		return referencias;
	};

	const addGrutors = (values) => {
		let avales = [];

		if (values.guarantorName1) {
			let aval = {
				guarantorName: values.guarantorName1,
				guarantorSSNumber: values.guarantorSS1,
				guarantorPosition: values.guarantorPosition1,
				guarantorDate: values.guarantorDate1
					? dayjs(values.guarantorDate1).toDate()
					: undefined,
				guarantorPhone: values.guarantorPhone1
					? `+1${values.guarantorPhone1}`
					: undefined,
				guarantorAddress: values.guarantorAddress1,
				guarantorCity: values.guarantorCity1,
				guarantorState: values.guarantorState1,
				guarantorZip: values.guarantorZip1,
			};
			avales.push(aval);
		}

		if (values.guarantorName2) {
			let aval = {
				guarantorName: values.guarantorName2,
				guarantorSSNumber: values.guarantorSS2,
				guarantorPosition: values.guarantorPosition2,
				guarantorDate: values.guarantorDate2
					? dayjs(values.guarantorDate2).toDate()
					: undefined,
				guarantorPhone: values.guarantorPhone2
					? `+1${values.guarantorPhone2}`
					: undefined,
				guarantorAddress: values.guarantorAddress2,
				guarantorCity: values.guarantorCity2,
				guarantorState: values.guarantorState2,
				guarantorZip: values.guarantorZip2,
			};
			avales.push(aval);
		}

		return avales;
	};

	const createNewCredit = async (values) => {
		setLoading(true);
		const formData = new FormData();

		formData.append("numberOfEmployees", values.numberOfEmployees);
		formData.append("amountOfCredit", values.amountOfCredit);
		formData.append("monthlyPurchases", values.monthlyPurchases);
		formData.append("termsOfSales", values.termsOfSales);
		formData.append(
			"businessStartDate",
			values.businessStartDate
				? dayjs(values.businessStartDate).toDate()
				: undefined,
		);

		formData.append("address", values.address);
		formData.append("city", values.city);
		formData.append("county", values.county);
		formData.append("state", values.state);
		formData.append("zip", values.zip);
		formData.append("sameAddress", values.sameAddress);
		formData.append("mailingAddress", values.mailingAddress);
		formData.append("mailingCity", values.mailingCity);
		formData.append("mailingCounty", values.mailingCounty);
		formData.append("mailingState", values.mailingState);
		formData.append("mailingZip", values.mailingZip);
		formData.append("email", values.email);
		formData.append("phone", values.phone ? `+1${values.phone}` : undefined);
		formData.append("fax", values.fax);

		formData.append("primaryContact", values.primaryContact);
		formData.append("primaryPosition", values.primaryPosition);
		formData.append(
			"primaryPhone",
			values.primaryPhone ? `+1${values.primaryPhone}` : undefined,
		);
		formData.append("primaryEmail", values.primaryEmail);
		formData.append("apContact", values.apContact);
		formData.append("apPosition", values.apPosition);
		formData.append(
			"apPhone",
			values.apPhone ? `+1${values.apPhone}` : undefined,
		);
		formData.append("apEmail", values.apEmail);
		formData.append("apEmailSecondary", values.apEmailSecondary);

		formData.append("businessType", values.businessType);
		formData.append("businessTypeOther", values.businessTypeOther);

		const owners = addOwners(values);
		if (owners.length > 0) {
			formData.append("owners", JSON.stringify(owners));
		}

		formData.append("bankName", values.bankName);
		formData.append("bankContact", values.bankContact);
		formData.append("bankAddress", values.bankAddress);
		formData.append(
			"bankPhone",
			values.bankPhone ? `+1${values.bankPhone}` : undefined,
		);
		formData.append("bankAccount", values.bankAccount);

		const references = addReferences(values);
		if (references.length > 0) {
			formData.append("references", JSON.stringify(references));
		}

		formData.append("poRequired", values.poRequired);
		formData.append("taxExempt", values.taxExempt);
		formData.append("invoiceMethod", values.invoiceMethod);
		formData.append("invoiceSendTo", values.invoiceSendTo);
		if (values.taxExemptCertificate && values.taxExemptCertificate.length > 0) {
			formData.append(
				"taxExemptCertificate",
				values.taxExemptCertificate[0].originFileObj,
			);
		}

		formData.append("signatureName", values.signatureName);
		formData.append("signatureEmail", values.signatureEmail);
		formData.append(
			"signaturePhone",
			values.signaturePhone ? `+1${values.signaturePhone}` : undefined,
		);
		formData.append("signatureDate", dayjs().toDate());
		// formData.append(
		// 	"signatureDate",
		// 	values.signatureDate ? dayjs(values.signatureDate).toDate() : undefined,
		// );

		// formData.append("createdBy", localStorage.getItem("userId"));

		const guarantors = addGrutors(values);

		if (guarantors.length > 0) {
			formData.append("guarantors", JSON.stringify(guarantors));
		}

		if (!signaturePadRef.current.isEmpty()) {
			const originalCanvas = signaturePadRef.current.getTrimmedCanvas();

			// Add a white background to the canvas
			const canvasWithWhiteBackground =
				getCanvasWithWhiteBackground(originalCanvas);

			// Convert the updated canvas to a data URL
			const signatureData = canvasWithWhiteBackground.toDataURL("image/png");

			// Convert Base64 to File
			const base64ToFile = (base64, filename) => {
				const byteString = atob(base64.split(",")[1]);
				const mimeString = base64.split(",")[0].split(":")[1].split(";")[0];
				const ab = new ArrayBuffer(byteString.length);
				const ia = new Uint8Array(ab);
				for (let i = 0; i < byteString.length; i++) {
					ia[i] = byteString.charCodeAt(i);
				}
				return new File([ab], filename, { type: mimeString });
			};

			const signatureFile = base64ToFile(signatureData, "signature.png");

			formData.append("signature", signatureFile);
		}
		if (!signatureFirstGuarantorPadRef.current.isEmpty()) {
			const originalCanvas =
				signatureFirstGuarantorPadRef.current.getTrimmedCanvas();

			// Add a white background to the canvas
			const canvasWithWhiteBackground =
				getCanvasWithWhiteBackground(originalCanvas);

			// Convert the updated canvas to a data URL
			const signatureData = canvasWithWhiteBackground.toDataURL("image/png");

			// Convert Base64 to File
			const base64ToFile = (base64, filename) => {
				const byteString = atob(base64.split(",")[1]);
				const mimeString = base64.split(",")[0].split(":")[1].split(";")[0];
				const ab = new ArrayBuffer(byteString.length);
				const ia = new Uint8Array(ab);
				for (let i = 0; i < byteString.length; i++) {
					ia[i] = byteString.charCodeAt(i);
				}
				return new File([ab], filename, { type: mimeString });
			};

			const signatureFile = base64ToFile(
				signatureData,
				"firstGuarantorSignature.png",
			);

			formData.append("firstGuarantorSignature", signatureFile);
		}
		if (!signatureSecondGuarantorPadRef.current.isEmpty()) {
			const originalCanvas =
				signatureSecondGuarantorPadRef.current.getTrimmedCanvas();

			// Add a white background to the canvas
			const canvasWithWhiteBackground =
				getCanvasWithWhiteBackground(originalCanvas);

			// Convert the updated canvas to a data URL
			const signatureData = canvasWithWhiteBackground.toDataURL("image/png");

			// Convert Base64 to File
			const base64ToFile = (base64, filename) => {
				const byteString = atob(base64.split(",")[1]);
				const mimeString = base64.split(",")[0].split(":")[1].split(";")[0];
				const ab = new ArrayBuffer(byteString.length);
				const ia = new Uint8Array(ab);
				for (let i = 0; i < byteString.length; i++) {
					ia[i] = byteString.charCodeAt(i);
				}
				return new File([ab], filename, { type: mimeString });
			};

			const signatureFile = base64ToFile(
				signatureData,
				"secondGuarantorSignature.png",
			);

			formData.append("secondGuarantorSignature", signatureFile);
		}

		try {
			const url = api + `/credit/create/${props.match.params.id}`;
			const token = localStorage.getItem(
				`credit_access_${props.match.params.id}`,
			);

			const config = {
				headers: {
					Authorization: `Bearer ${token}`,
					"Content-Type": "multipart/form-data",
				},
			};

			const { data } = await axios.post(url, formData, config);
			if (data.success) {
				openNotification("success", "Success", data.message);
				await validationOnUrl();
			} else {
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			setLoading(false);
			openNotification("error", "Error", err.message);
			console.error(err.message);
			return null;
		}
	};

	const getCanvasWithWhiteBackground = (canvas) => {
		const newCanvas = document.createElement("canvas");
		const context = newCanvas.getContext("2d");

		// Set canvas dimensions
		newCanvas.width = canvas.width;
		newCanvas.height = canvas.height;

		// Fill the background with white
		context.fillStyle = "#fff";
		context.fillRect(0, 0, newCanvas.width, newCanvas.height);

		// Draw the signature on top
		context.drawImage(canvas, 0, 0);

		return newCanvas;
	};

	const onChangeSameAddress = (e) => {
		const checked = e.target.checked;
		if (checked) {
			const { address, city, county, state, zip } = form.getFieldsValue([
				"address",
				"city",
				"county",
				"state",
				"zip",
			]);

			form.setFieldsValue({
				mailingAddress: address,
				mailingCity: city,
				mailingCounty: county,
				mailingState: state,
				mailingZip: zip,
			});
		} else {
			form.setFieldsValue({
				mailingAddress: undefined,
				mailingCity: undefined,
				mailingCounty: undefined,
				mailingState: undefined,
				mailingZip: undefined,
			});
		}
	};

	const onFinish = () => {
		if (signaturePadRef.current.isEmpty()) {
			openNotification(
				"error",
				"Error",
				"Please provide a signature before submitting.",
			);
			return;
		}
		if (signatureFirstGuarantorPadRef.current.isEmpty()) {
			openNotification(
				"error",
				"Error",
				"Please provide first guarantor signature before submitting.",
			);
			return;
		}
		if (signatureSecondGuarantorPadRef.current.isEmpty()) {
			openNotification(
				"error",
				"Error",
				"Please provide second guarantor signature before submitting.",
			);

			return;
		}

		const values = form.getFieldsValue();
		createNewCredit(values);
	};

	const prefixSelector = (
		<Form.Item name="prefix" noStyle>
			<Select
				style={{
					width: 70,
				}}
			>
				<Select.Option value="+1">+1</Select.Option>
			</Select>
		</Form.Item>
	);

	const filterOption = (input, option) =>
		(option?.label ?? "").toLowerCase().includes(input.toLowerCase());

	const normFile = (e) => {
		if (Array.isArray(e)) {
			return e;
		}
		return e?.fileList;
	};

	const CreditMadeScreen = () => {
		return (
			<Result
				status="success"
				title="Credit Application Created"
				subTitle={`Thanks for submitting your credit form`}
			/>
		);
	};

	if (otpVerified === null) {
		return <CustomSpinner />;
	}

	return (
		<Layout title="Credit Application Form" description="Fill in the form">
			{!otpVerified ? (
				<OTPScreen />
			) : isLoading ? (
				<CustomSpinner />
			) : creditMade ? (
				<CreditMadeScreen />
			) : (
				<Spin spinning={loading}>
					<Form
						form={form}
						layout="vertical"
						onFinish={onFinish}
						style={{ marginTop: "20px" }}
					>
						<Row gutter={16}>
							<Col span={24} md={12}>
								<Form.Item
									label="Legal Company Name"
									name="name"
									rules={[
										{
											required: true,
											message: "Please fill company name",
										},
									]}
								>
									<Input size="large" disabled />
								</Form.Item>
							</Col>
							<Col span={24} md={6}>
								<Form.Item
									label="DBA"
									name="dba"
									rules={[
										{
											required: true,
											message: "Please fill DBA",
										},
									]}
								>
									<Input size="large" disabled />
								</Form.Item>
							</Col>
							<Col span={24} md={6}>
								<Form.Item
									label="EIN/Tax ID (W-9)"
									name="taxId"
									rules={[
										{
											required: true,
											message: "Please fill EIN/Tax ID",
										},
									]}
								>
									<Input size="large" disabled />
								</Form.Item>
							</Col>
							<Col span={24} md={12}>
								<Form.Item
									label="Federal Tax Classification"
									name="federalTaxClassification"
									rules={[
										{
											required: true,
											message: "Please select federal tax classification",
										},
									]}
								>
									<Select
										showSearch
										placeholder={`Select federal tax classification`}
										optionFilterProp="children"
										size="large"
										filterOption={filterOption}
										options={federalTaxClassificationOptions}
										style={{
											width: "100%",
										}}
										disabled
									/>
								</Form.Item>
							</Col>
							<Col span={24} md={12}>
								<Form.Item label="Date of Submission" name="date">
									<DatePicker
										size="large"
										disabled
										format="MM/DD/YYYY hh:mm A"
										style={{
											width: "100%",
										}}
									/>
								</Form.Item>
							</Col>
						</Row>
						<Row gutter={16}>
							<Col span={24} md={5}>
								<Form.Item
									label="Amount of Credit Requested ($)"
									name="amountOfCredit"
									rules={[
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<InputNumber
										size="large"
										style={{ width: "100%" }}
										formatter={(value) =>
											`$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
										}
										parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
									/>
								</Form.Item>
							</Col>
							<Col span={24} md={5}>
								<Form.Item
									label="Estimated Monthly Purchases ($)"
									name="monthlyPurchases"
									rules={[
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<InputNumber
										size="large"
										style={{ width: "100%" }}
										formatter={(value) =>
											`$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
										}
										parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
									/>
								</Form.Item>
							</Col>
							<Col span={24} md={5}>
								<Form.Item
									label="Business Start Date"
									name="businessStartDate"
									rules={[
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<DatePicker
										size="large"
										format={"MM/DD/YYYY"}
										style={{ width: "100%" }}
									/>
								</Form.Item>
							</Col>
							<Col span={24} md={5}>
								<Form.Item
									label="Terms of Sales Requested (days)"
									name="termsOfSales"
									rules={[
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<InputNumber
										size="large"
										style={{ width: "100%" }}
										formatter={(value) =>
											`${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
										}
										parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
									/>
								</Form.Item>
							</Col>
							<Col span={24} md={4}>
								<Form.Item
									label="Number of Employees"
									name="numberOfEmployees"
									rules={[
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<InputNumber
										size="large"
										style={{ width: "100%" }}
										formatter={(value) =>
											`${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
										}
										parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
									/>
								</Form.Item>
							</Col>
						</Row>
						<div style={{ backgroundColor: "lightgrey" }}>
							<center>Business Address</center>
						</div>
						<Row gutter={16}>
							<Col span={24} sm={8}>
								<Form.Item
									label="Street Address"
									name="address"
									rules={[
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<Input size="large" />
								</Form.Item>
							</Col>
							<Col span={24} sm={4}>
								<Form.Item
									label="City"
									name="city"
									rules={[
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<Input size="large" />
								</Form.Item>
							</Col>
							<Col span={24} sm={4}>
								<Form.Item
									label="County"
									name="county"
									rules={[
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<Input size="large" />
								</Form.Item>
							</Col>
							<Col span={24} sm={4}>
								<Form.Item
									label="State"
									name="state"
									rules={[
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<Input size="large" />
								</Form.Item>
							</Col>
							<Col span={24} sm={4}>
								<Form.Item
									label="Zip"
									name="zip"
									rules={[
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<Input size="large" />
								</Form.Item>
							</Col>
							<Col span={24}>
								<Form.Item name="sameAddress" valuePropName="checked">
									<Checkbox onChange={onChangeSameAddress}>
										Mailing Address same as Street Address
									</Checkbox>
								</Form.Item>
							</Col>
						</Row>
						<Row gutter={16}>
							<Col span={24} sm={8}>
								<Form.Item label="Mailing Address" name="mailingAddress">
									<Input size="large" />
								</Form.Item>
							</Col>
							<Col span={24} sm={4}>
								<Form.Item label="City" name="mailingCity">
									<Input size="large" />
								</Form.Item>
							</Col>
							<Col span={24} sm={4}>
								<Form.Item label="County" name="mailingCounty">
									<Input size="large" />
								</Form.Item>
							</Col>
							<Col span={24} sm={4}>
								<Form.Item label="State" name="mailingState">
									<Input size="large" />
								</Form.Item>
							</Col>
							<Col span={24} sm={4}>
								<Form.Item label="Zip" name="mailingZip">
									<Input size="large" />
								</Form.Item>
							</Col>
						</Row>
						<Row gutter={16}>
							<Col span={24} sm={8}>
								<Form.Item
									label="Email"
									name="email"
									rules={[{ type: "email", message: "Email not valid" }]}
								>
									<Input size="large" />
								</Form.Item>
							</Col>
							<Col span={24} sm={8}>
								<Form.Item label="Phone" name="phone">
									<Input size="large" addonBefore={prefixSelector} />
								</Form.Item>
							</Col>
							<Col span={24} sm={8}>
								<Form.Item label="Fax" name="fax">
									<Input size="large" />
								</Form.Item>
							</Col>
						</Row>
						<div style={{ backgroundColor: "lightgrey" }}>
							<center>Key Contacts</center>
						</div>
						<Row gutter={16}>
							<Col span={24} sm={5}>
								<Form.Item
									label="Primary Contact"
									name="primaryContact"
									rules={[
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<Input size="large" />
								</Form.Item>
							</Col>
							<Col span={24} sm={5}>
								<Form.Item
									label="Title/Position"
									name="primaryPosition"
									rules={[
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<Input size="large" />
								</Form.Item>
							</Col>
							<Col span={24} sm={5}>
								<Form.Item
									label="Phone"
									name="primaryPhone"
									rules={[
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<Input size="large" addonBefore={prefixSelector} />
								</Form.Item>
							</Col>
							<Col span={24} sm={5}>
								<Form.Item
									label="Primary Email"
									name="primaryEmail"
									rules={[
										{ type: "email", message: "Email not valid" },
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<Input size="large" />
								</Form.Item>
							</Col>
						</Row>
						<Row gutter={16}>
							<Col span={24} sm={5}>
								<Form.Item
									label="AP Contact"
									name="apContact"
									rules={[
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<Input size="large" />
								</Form.Item>
							</Col>
							<Col span={24} sm={5}>
								<Form.Item
									label="Title/Position"
									name="apPosition"
									rules={[
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<Input size="large" />
								</Form.Item>
							</Col>
							<Col span={24} sm={5}>
								<Form.Item
									label="Phone"
									name="apPhone"
									rules={[
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<Input size="large" addonBefore={prefixSelector} />
								</Form.Item>
							</Col>
							<Col span={24} sm={5}>
								<Form.Item
									label="Email"
									name="apEmail"
									rules={[
										{ type: "email", message: "Email not valid" },
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<Input size="large" />
								</Form.Item>
							</Col>
							<Col span={24} sm={4}>
								<Form.Item
									label="Secondary Email"
									name="apEmailSecondary"
									rules={[
										{ type: "email", message: "Email not valid" },
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<Input size="large" />
								</Form.Item>
							</Col>
						</Row>

						<div style={{ backgroundColor: "lightgrey" }}>
							<center>Type of Business</center>
						</div>
						<Row gutter={16}>
							<Col span={24} sm={18}>
								<Form.Item
									label="Business Type"
									name="businessType"
									rules={[
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<Select
										options={businessTypeOptions}
										placeholder="Select business type"
										style={{ width: "100%" }}
										size="large"
										onChange={(value) => setBusinessType(value)}
									/>
								</Form.Item>
							</Col>
							<Col span={24} sm={6}>
								<Form.Item label="Other" name="businessTypeOther">
									<Input size="large" disabled={businessType !== "Other"} />
								</Form.Item>
							</Col>
						</Row>

						<div style={{ backgroundColor: "lightgrey" }}>
							<center>Officers / Owners</center>
						</div>
						<Row gutter={16}>
							<Col span={24} sm={6}>
								<Form.Item
									label="Name"
									name="ownerName1"
									rules={[
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<Input size="large" />
								</Form.Item>
							</Col>
							<Col span={24} sm={4}>
								<Form.Item
									label="Title/Position"
									name="ownerPosition1"
									rules={[
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<Input size="large" />
								</Form.Item>
							</Col>
							<Col span={24} sm={2}>
								<Form.Item
									label="% Owned"
									name="ownerOwned1"
									rules={[
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<InputNumber
										size="large"
										style={{ width: "100%" }}
										formatter={(value) =>
											`${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
										}
										parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
									/>
								</Form.Item>
							</Col>
							<Col span={24} sm={8}>
								<Form.Item
									label="Home address / City / State / ZIP"
									name="ownerAddress1"
									rules={[
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<Input size="large" />
								</Form.Item>
							</Col>
							<Col span={24} sm={4}>
								<Form.Item
									label="Phone"
									name="ownerPhone1"
									rules={[
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<Input size="large" addonBefore={prefixSelector} />
								</Form.Item>
							</Col>
							<Col span={24} sm={6}>
								<Form.Item label="Name" name="ownerName2">
									<Input size="large" />
								</Form.Item>
							</Col>
							<Col span={24} sm={4}>
								<Form.Item label="Title/Position" name="ownerPosition2">
									<Input size="large" />
								</Form.Item>
							</Col>
							<Col span={24} sm={2}>
								<Form.Item label="% Owned" name="ownerOwned2">
									<InputNumber
										size="large"
										style={{ width: "100%" }}
										formatter={(value) =>
											`${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
										}
										parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
									/>
								</Form.Item>
							</Col>
							<Col span={24} sm={8}>
								<Form.Item
									label="Home address / City / State / ZIP"
									name="ownerAddress2"
								>
									<Input size="large" />
								</Form.Item>
							</Col>
							<Col span={24} sm={4}>
								<Form.Item label="Phone" name="ownerPhone2">
									<Input size="large" addonBefore={prefixSelector} />
								</Form.Item>
							</Col>
							<Col span={24} sm={6}>
								<Form.Item label="Name" name="ownerName3">
									<Input size="large" />
								</Form.Item>
							</Col>
							<Col span={24} sm={4}>
								<Form.Item label="Title/Position" name="ownerPosition3">
									<Input size="large" />
								</Form.Item>
							</Col>
							<Col span={24} sm={2}>
								<Form.Item label="% Owned" name="ownerOwned3">
									<InputNumber
										size="large"
										style={{ width: "100%" }}
										formatter={(value) =>
											`${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
										}
										parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
									/>
								</Form.Item>
							</Col>
							<Col span={24} sm={8}>
								<Form.Item
									label="Home address / City / State / ZIP"
									name="ownerAddress3"
								>
									<Input size="large" />
								</Form.Item>
							</Col>
							<Col span={24} sm={4}>
								<Form.Item label="Phone" name="ownerPhone3">
									<Input size="large" addonBefore={prefixSelector} />
								</Form.Item>
							</Col>
						</Row>
						<div style={{ backgroundColor: "lightgrey" }}>
							<center>Bank References</center>
						</div>
						<Row gutter={16}>
							<Col span={24} sm={5}>
								<Form.Item
									label="Name"
									name="bankName"
									rules={[
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<Input size="large" />
								</Form.Item>
							</Col>
							<Col span={24} sm={5}>
								<Form.Item
									label="Bank Officer/Contact"
									name="bankContact"
									rules={[
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<Input size="large" />
								</Form.Item>
							</Col>
							<Col span={24} sm={5}>
								<Form.Item
									label="Address / City / State / ZIP"
									name="bankAddress"
									rules={[
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<Input size="large" />
								</Form.Item>
							</Col>
							<Col span={24} sm={5}>
								<Form.Item
									label="Phone"
									name="bankPhone"
									rules={[
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<Input size="large" addonBefore={prefixSelector} />
								</Form.Item>
							</Col>
							<Col span={24} sm={4}>
								<Form.Item
									label="Account #"
									name="bankAccount"
									rules={[
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<Input size="large" />
								</Form.Item>
							</Col>
						</Row>
						<div style={{ backgroundColor: "lightgrey" }}>
							<center>Trade References</center>
						</div>
						<Row gutter={16}>
							<Col span={24} sm={6}>
								<Form.Item
									label="Business Name"
									name="referenceName1"
									rules={[
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<Input size="large" />
								</Form.Item>
							</Col>
							<Col span={24} sm={6}>
								<Form.Item
									label="Address / City / State / ZIP"
									name="referenceAddress1"
									rules={[
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<Input size="large" />
								</Form.Item>
							</Col>
							<Col span={24} sm={6}>
								<Form.Item
									label="Phone"
									name="referencePhone1"
									rules={[
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<Input size="large" addonBefore={prefixSelector} />
								</Form.Item>
							</Col>
							<Col span={24} sm={6}>
								<Form.Item
									label="Email"
									name="referenceEmail1"
									rules={[
										{ type: "email", message: "Email not valid" },
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<Input size="large" />
								</Form.Item>
							</Col>
							<Col span={24} sm={6}>
								<Form.Item label="Business Name" name="referenceName2">
									<Input size="large" />
								</Form.Item>
							</Col>
							<Col span={24} sm={6}>
								<Form.Item
									label="Address / City / State / ZIP"
									name="referenceAddress2"
								>
									<Input size="large" />
								</Form.Item>
							</Col>
							<Col span={24} sm={6}>
								<Form.Item label="Phone" name="referencePhone2">
									<Input size="large" addonBefore={prefixSelector} />
								</Form.Item>
							</Col>
							<Col span={24} sm={6}>
								<Form.Item
									label="Email"
									name="referenceEmail2"
									rules={[{ type: "email", message: "Email not valid" }]}
								>
									<Input size="large" />
								</Form.Item>
							</Col>
							<Col span={24} sm={6}>
								<Form.Item label="Business Name" name="referenceName3">
									<Input size="large" />
								</Form.Item>
							</Col>
							<Col span={24} sm={6}>
								<Form.Item
									label="Address / City / State / ZIP"
									name="referenceAddress3"
								>
									<Input size="large" />
								</Form.Item>
							</Col>
							<Col span={24} sm={6}>
								<Form.Item label="Phone" name="referencePhone3">
									<Input size="large" addonBefore={prefixSelector} />
								</Form.Item>
							</Col>
							<Col span={24} sm={6}>
								<Form.Item
									label="Email"
									name="referenceEmail3"
									rules={[{ type: "email", message: "Email not valid" }]}
								>
									<Input size="large" />
								</Form.Item>
							</Col>
						</Row>
						<div style={{ backgroundColor: "lightgrey" }}>
							<center>Tax and Invoicing</center>
						</div>
						<Row gutter={16}>
							<Col span={24} sm={4}>
								<Form.Item label="Taxable?" name="taxExempt">
									<Radio.Group>
										<Radio value={true}>Yes</Radio>
										<Radio value={false}>No</Radio>
									</Radio.Group>
								</Form.Item>
							</Col>
							<Col span={24} md={20}>
								<Form.Item
									label="If yes, Attach tax exempt certificate"
									name="taxExemptCertificate"
									valuePropName="fileList"
									getValueFromEvent={normFile}
								>
									<Upload
										beforeUpload={() => false}
										maxCount={1}
										multiple={false}
										accept=".pdf"
									>
										<Button icon={<UploadOutlined />} size="large">
											Upload
										</Button>
									</Upload>
								</Form.Item>
							</Col>
							<Col span={24} sm={4}>
								<Form.Item label="PO Required?" name="poRequired">
									<Radio.Group>
										<Radio value={true}>Yes</Radio>
										<Radio value={false}>No</Radio>
									</Radio.Group>
								</Form.Item>
							</Col>
							<Col span={24} sm={4}>
								<Form.Item label="Invoice Method:" name="invoiceMethod">
									<Radio.Group>
										<Radio value={"Email"}>Email</Radio>
										<Radio value={"Mail"}>Mail</Radio>
									</Radio.Group>
								</Form.Item>
							</Col>
							<Col span={24} sm={6}>
								<Form.Item
									label="Send To (Email or Mailing Address)"
									name="invoiceSendTo"
									rules={[
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<Input size="large" />
								</Form.Item>
							</Col>
						</Row>
						<div style={{ backgroundColor: "lightgrey" }}>
							<center>Buyer Certification & Agreement</center>
						</div>
						<div>
							<small>
								Buyer certifies that it is solvent and capable of meeting its
								obligations hereunder, and that all information (including any
								requested financial statements) provided to 57 Concrete, LLC
								and/or its subsidiaries and affiliates ("Seller") is true,
								accurate and complete. All such information has been submitted
								for the purpose of obtaining credit. Buyer and each Guarantor
								authorize Seller to request credit reports from credit bureaus
								(including consumer reporting agencies) regarding their
								respective commercial or personal credit and otherwise to
								investigate their respective creditworthiness before extending
								credit now or at any time in the future. Buyer also agrees to
								comply with all applicable bulk sales laws. Buyer agrees that
								all Terms and Conditions of Sale on the reverse side of this
								Credit Application, as the same may be amended from time to
								time, shall apply to all sales and extensions of credit made to
								Buyer by Seller. Buyer agrees that all Terms and Conditions of
								Sale on Page 4 of this Credit Application, as the same may be
								amended from time to time, shall apply to all sales and
								extensions of credit made to Buyer by Seller.
							</small>
						</div>
						<div style={{ backgroundColor: "lightgrey" }}>
							<center>Authorized Signature (Applicant)</center>
						</div>
						<Row gutter={16}>
							<Col span={24} sm={6}>
								<Form.Item
									label="Name/Title"
									name="signatureName"
									rules={[
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<Input size="large" placeholder="Signature Name" />
								</Form.Item>
							</Col>
							<Col span={24} sm={6}>
								<Form.Item
									label="Email"
									name="signatureEmail"
									rules={[
										{ type: "email", message: "Email not valid" },
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<Input size="large" />
								</Form.Item>
							</Col>
							<Col span={24} sm={6}>
								<Form.Item
									label="Phone"
									name="signaturePhone"
									rules={[
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<Input size="large" addonBefore={prefixSelector} />
								</Form.Item>
							</Col>
							{/* <Col span={24} sm={6}>
								<Form.Item
									label="Date"
									name="signatureDate"
									rules={[
										{
											required: true,
											message: "Field required",
										},
									]}
								>
									<DatePicker
										size="large"
										style={{ width: "100%" }}
										format={"MM/DD/YYYY"}
									/>
								</Form.Item>
							</Col> */}
							<Col span={24}>
								<Form.Item label="Signature" required>
									<div
										style={{
											border: "1px solid #d9d9d9",
											padding: 10,
											borderRadius: 4,
										}}
									>
										<SignatureCanvas
											ref={signaturePadRef}
											penColor="black"
											backgroundColor="rgba(255,255,255,1)"
											canvasProps={{
												style: { width: "100%" },
												height: 400,
												className: "sigCanvas",
											}}
										/>
									</div>
									<Space style={{ marginTop: 10 }}>
										<Button
											type="default"
											htmlType="button"
											onClick={handleClear}
											size="large"
										>
											Clear Signature
										</Button>
									</Space>
								</Form.Item>
							</Col>
						</Row>
						<div style={{ backgroundColor: "lightgrey" }}>
							<center>
								<strong>TERMS AND CONDITIONS OF SALE</strong> (“Terms”)
							</center>
						</div>
						<div className="small">
							<ol>
								<li>
									All references in this document to "Seller" shall include 57
									Concrete LLC and/or any parent, subsidiary or affiliate of 57
									Concrete LLC (including any division of the foregoing) whether
									or not performing any or all of the scope hereunder or
									specifically identified herein. All references to "Buyer"
									shall include all parent(s), subsidiaries and affiliates of
									the entity placing the order. Buyer and Seller may be referred
									to individually as a "Party" and collectively as "Parties".
								</li>
								<li>
									All sales to Buyer are subject to these Terms, which shall
									prevail over any inconsistent terms of Buyer's purchase order
									or other documents. Additional or different terms and
									conditions in any way altering or modifying these Terms are
									expressly objected to and shall not be binding upon Seller
									unless specifically accepted in writing by Seller's authorized
									representative. No modification or alteration of these Terms
									shall result by Seller's shipment of goods following receipt
									of Buyer's purchase order, or other documents containing
									additional, conflicting or inconsistent terms. There are no
									terms, conditions, understandings, or agreements other than
									those stated herein, and all prior proposals and negotiations
									are merged herein. These Terms are binding on the Parties,
									their successors, and permitted assigns.
								</li>
								<li>
									Prices in Seller quotes are subject to change without notice,
									and all such prices expire and become invalid if not accepted
									within 10 calendar days from the date of issue, unless
									otherwise noted by Seller in writing. Price extensions if made
									must be in writing only. All prices and other terms provided
									to Buyer shall be kept confidential except to the extent a
									Party is required by law to disclose the same.
								</li>
								<li>
									Seller shall not be liable for delay or default in delivery
									resulting from any cause beyond Seller's reasonable control,
									including, but not limited to, governmental action, strikes or
									other labor troubles, fire, damage or destruction of goods,
									wars (declared or undeclared), acts of terrorism,
									manufacturers' shortages, availability or timeliness of
									transportation, materials, fuels, or supplies, and acts of God
									(each a "Force Majeure Event"). Upon the occurrence of a Force
									Majeure Event: (a) the time for Seller's performance shall be
									extended reasonably and the Parties shall adjust all affected
									dates accordingly; (b) the purchase price shall be adjusted
									for any increased costs to Seller resulting from such Force
									Majeure Event; and (c) Buyer shall not be entitled to any
									other remedy.
								</li>
								<li>
									Buyer shall indemnify, defend, and hold Seller its officers,
									directors, employees and agents harmless from any and all
									costs (including attorneys' and accountants' fees and
									expenses), liabilities and damages resulting from or related
									to any third party (including Buyer's employees) claim,
									complaint and/or judgment arising from Buyer's use of any
									goods furnished hereunder, as well as any negligent,
									intentional, or tortious act or omission of Buyer or any
									material breach by Buyer of these Terms.
								</li>
								<li>
									When materials are delivered to Buyer, Buyer shall sign each
									individual delivery ticket to ensure proper accounting at the
									time of invoicing. Title and risk of loss shall pass to Buyer
									at the applicable delivery location. All claims as to which
									Seller has the risk of loss shall be waived unless Buyer,
									within 2 calendar days after receipt of the material, gives
									Seller written notice fully describing the alleged claim.
								</li>
								<li>
									Any change in product specifications, quantities,
									destinations, shipping schedules, or any other aspect of the
									scope of goods must be agreed to in writing by Seller, and may
									result in a price and delivery adjustment by Seller. No credit
									for goods returned by Buyer shall be given without Seller's
									written authorization.
								</li>
								<li>
									Unless otherwise agreed in writing, payment terms are Cash on
									Delivery, payable in United States of America ("U.S.")
									dollars. Notwithstanding the foregoing, all orders are subject
									to Seller's continuing approval of Buyer's credit. If Buyer's
									credit is not approved or becomes unsatisfactory to Seller
									then Seller, in its sole discretion, may suspend or cancel
									performance, or require different payment terms, including but
									not limited to cash on delivery or in advance of shipment.
									Payments due hereunder shall be made in the form of cash,
									check, wire transfer or money order, or other tender approved
									in writing by Seller. Seller may, in its sole discretion,
									apply Buyer's payment against any open charges. Past due
									accounts bear interest at the lesser of 4.0% per day or the
									maximum rate permitted by applicable law, continuing after
									Seller obtains judgment against Buyer. Seller may exercise
									setoff or recoupment to apply to or satisfy Buyer's
									outstanding debt. Buyer shall have no right of setoff
									hereunder, the same being expressly waived hereby. Seller
									expressly reserves its right to file liens if payment is not
									received for its materials and expressly disclaims any waiver
									of lien rights language which may be contained in any future
									agreements between the Parties hereto. Seller reserves all
									rights to invoice and be paid for materials provided to Buyer
									and any terms contained in any of Buyer's purchase orders or
									other documents that purport to limit in any way the time or
									manner within which Seller may invoice are hereby waived by
									Buyer.
								</li>
								<li>
									Buyer shall pay Seller all costs and expenses of collection,
									suit, or other legal action brought as a result of the
									commercial relationship between them, including, but not
									limited to, all actual attorneys' and paralegals' fees, and
									collection costs, incurred pre-suit, through trial, on appeal,
									and in any administrative or bankruptcy proceedings. Any cause
									of action that Seller has against Buyer may be assigned
									without Buyer's consent to 57 Concrete LLC or to any
									affiliate, parent or subsidiary of 57 Concrete LLC.
								</li>
								<li>
									This Agreement, Buyer's account, and the business relationship
									between Buyer and Seller shall be governed by and construed in
									accordance with the laws of the state where the applicable
									project is located without regard to conflicts of laws rules,
									and specifically excluding the UN Convention on Contracts for
									the International Sale of Goods. The Parties agree that any
									legal action arising under or related to this Agreement may be
									brought in the applicable federal or state court where the
									project is located, and any right to object to such venue or
									to assert the inconvenience of such forum is hereby waived.
								</li>
								<li>
									If Buyer fails to comply with these Terms, Seller may
									terminate or restrict any order immediately upon notice to
									Buyer. Buyer certifies that it is solvent and that it will
									advise Seller immediately if it becomes insolvent. Buyer
									agrees to send Seller written notice of any changes in the
									form of ownership of Buyer's business within 5 days of such
									changes. Buyer and Seller are the only intended beneficiaries
									of this document, and there are no third party beneficiaries.
								</li>
								<li>
									The invalidity or unenforceability of all or part of these
									Terms will not affect the validity or enforceability of the
									other terms. The parties agree to replace any void or
									unenforceable term with a new term that achieves substantially
									the same practical and economic effect and is valid and
									enforceable.
								</li>
								<li>
									The following provisions shall survive termination,
									cancellation and completed performance of this Agreement as
									long as necessary to allow the aggrieved party to fully
									enforce such clauses: 5, 6, 9, 10, 11 and 12.
								</li>
							</ol>
						</div>
						<div>
							<div style={{ backgroundColor: "lightgrey" }}>
								<center>Personal Guaranty</center>
							</div>
							<div className="small">
								In consideration of the extension of credit by 57 Concrete to
								the applicant named above, the undersigned, jointly and
								severally, unconditionally guarantee the prompt payment of all
								amounts now or hereafter owed by the applicant to 57 Concrete.
								This is a continuing guaranty. The undersigned waives notice of
								acceptance and demand, and authorizes 57 Concrete to obtain
								consumer credit reports on the undersigned in connection with
								this guaranty.
							</div>
							<Row gutter={16}>
								<Col span={24} sm={5}>
									<Form.Item
										label="First Guarantor's Name"
										name="guarantorName1"
										rules={[
											{
												required: true,
												message: "Field required",
											},
										]}
									>
										<Input size="large" placeholder="First guarantor Name" />
									</Form.Item>
								</Col>
								<Col span={24} sm={5}>
									<Form.Item
										label="Title"
										name="guarantorPosition1"
										rules={[
											{
												required: true,
												message: "Field required",
											},
										]}
									>
										<Input size="large" placeholder="First guarantor Title" />
									</Form.Item>
								</Col>
								<Col span={24} sm={5}>
									<Form.Item
										label="Date of Birth"
										name="guarantorDate1"
										rules={[
											{
												required: true,
												message: "Field required",
											},
										]}
									>
										<DatePicker
											size="large"
											style={{ width: "100%" }}
											format={"MM/DD/YYYY"}
										/>
									</Form.Item>
								</Col>
								<Col span={24} sm={5}>
									<Form.Item
										label="S/S Number"
										name="guarantorSS1"
										rules={[
											{
												required: true,
												message: "Field required",
											},
										]}
									>
										<Input
											size="large"
											placeholder="First guarantor S/S Number"
										/>
									</Form.Item>
								</Col>
								<Col span={24} sm={4}>
									<Form.Item
										label="Phone"
										name="guarantorPhone1"
										rules={[
											{
												required: true,
												message: "Field required",
											},
										]}
									>
										<Input
											size="large"
											placeholder="First Guarantor Phone"
											addonBefore={prefixSelector}
										/>
									</Form.Item>
								</Col>
								<Col span={24} sm={6}>
									<Form.Item
										label="Address"
										name="guarantorAddress1"
										rules={[
											{
												required: true,
												message: "Field required",
											},
										]}
									>
										<Input size="large" placeholder="First Guarantor Address" />
									</Form.Item>
								</Col>
								<Col span={24} sm={6}>
									<Form.Item
										label="City"
										name="guarantorCity1"
										rules={[
											{
												required: true,
												message: "Field required",
											},
										]}
									>
										<Input size="large" placeholder="First Guarantor City" />
									</Form.Item>
								</Col>
								<Col span={24} sm={6}>
									<Form.Item
										label="State"
										name="guarantorState1"
										rules={[
											{
												required: true,
												message: "Field required",
											},
										]}
									>
										<Input size="large" placeholder="First Guarantor State" />
									</Form.Item>
								</Col>
								<Col span={24} sm={6}>
									<Form.Item
										label="Zip"
										name="guarantorZip1"
										rules={[
											{
												required: true,
												message: "Field required",
											},
										]}
									>
										<Input size="large" placeholder="First Guarantor Zip" />
									</Form.Item>
								</Col>
								<Col span={24}>
									<Form.Item label="First Guarantor Signature" required>
										<div
											style={{
												border: "1px solid #d9d9d9",
												padding: 10,
												borderRadius: 4,
											}}
										>
											<SignatureCanvas
												ref={signatureFirstGuarantorPadRef}
												penColor="black"
												backgroundColor="rgba(255,255,255,1)"
												canvasProps={{
													style: { width: "100%" },
													height: 400,
													className: "sigCanvas",
												}}
											/>
										</div>
										<Space style={{ marginTop: 10 }}>
											<Button
												type="default"
												htmlType="button"
												onClick={handleFirstGuarantorClear}
												size="large"
											>
												Clear Signature
											</Button>
										</Space>
									</Form.Item>
								</Col>
							</Row>
							<Row gutter={16}>
								<Col span={24} sm={5}>
									<Form.Item
										label="Second Guarantor's Name"
										name="guarantorName2"
										rules={[
											{
												required: true,
												message: "Field required",
											},
										]}
									>
										<Input size="large" placeholder="Second guarantor Name" />
									</Form.Item>
								</Col>
								<Col span={24} sm={5}>
									<Form.Item
										label="Title"
										name="guarantorPosition2"
										rules={[
											{
												required: true,
												message: "Field required",
											},
										]}
									>
										<Input size="large" placeholder="Second guarantor Title" />
									</Form.Item>
								</Col>
								<Col span={24} sm={5}>
									<Form.Item
										label="Date of Birth"
										name="guarantorDate2"
										rules={[
											{
												required: true,
												message: "Field required",
											},
										]}
									>
										<DatePicker
											size="large"
											style={{ width: "100%" }}
											format={"MM/DD/YYYY"}
										/>
									</Form.Item>
								</Col>
								<Col span={24} sm={5}>
									<Form.Item
										label="S/S Number"
										name="guarantorSS2"
										rules={[
											{
												required: true,
												message: "Field required",
											},
										]}
									>
										<Input
											size="large"
											placeholder="Second guarantor S/S Number"
										/>
									</Form.Item>
								</Col>
								<Col span={24} sm={4}>
									<Form.Item
										label="Phone"
										name="guarantorPhone2"
										rules={[
											{
												required: true,
												message: "Field required",
											},
										]}
									>
										<Input
											size="large"
											placeholder="Second Guarantor Phone"
											addonBefore={prefixSelector}
										/>
									</Form.Item>
								</Col>
								<Col span={24} sm={6}>
									<Form.Item
										label="Address"
										name="guarantorAddress2"
										rules={[
											{
												required: true,
												message: "Field required",
											},
										]}
									>
										<Input
											size="large"
											placeholder="Second Guarantor Address"
										/>
									</Form.Item>
								</Col>
								<Col span={24} sm={6}>
									<Form.Item
										label="City"
										name="guarantorCity2"
										rules={[
											{
												required: true,
												message: "Field required",
											},
										]}
									>
										<Input size="large" placeholder="Second Guarantor City" />
									</Form.Item>
								</Col>
								<Col span={24} sm={6}>
									<Form.Item
										label="State"
										name="guarantorState2"
										rules={[
											{
												required: true,
												message: "Field required",
											},
										]}
									>
										<Input size="large" placeholder="Second Guarantor State" />
									</Form.Item>
								</Col>
								<Col span={24} sm={6}>
									<Form.Item
										label="Zip"
										name="guarantorZip2"
										rules={[
											{
												required: true,
												message: "Field required",
											},
										]}
									>
										<Input size="large" placeholder="Second Guarantor Zip" />
									</Form.Item>
								</Col>
								<Col span={24}>
									<Form.Item label="Second Guarantor Signature" required>
										<div
											style={{
												border: "1px solid #d9d9d9",
												padding: 10,
												borderRadius: 4,
											}}
										>
											<SignatureCanvas
												ref={signatureSecondGuarantorPadRef}
												penColor="black"
												backgroundColor="rgba(255,255,255,1)"
												canvasProps={{
													style: { width: "100%" },
													height: 400,
													className: "sigCanvas",
												}}
											/>
										</div>
										<Space style={{ marginTop: 10 }}>
											<Button
												type="default"
												htmlType="button"
												onClick={handleSecondGuarantorClear}
												size="large"
											>
												Clear Signature
											</Button>
										</Space>
									</Form.Item>
								</Col>
							</Row>
						</div>
						<Divider />
						<Row gutter={16}>
							<Col span={24}>
								<Form.Item
									label=""
									name="acceptConditions"
									valuePropName="checked"
								>
									<Checkbox
										onChange={(value) =>
											setAcceptConditions(value.target.checked)
										}
									>
										Accept terms and conditions in order to proceed.
									</Checkbox>
								</Form.Item>
							</Col>
						</Row>

						<Row gutter={16}>
							<Col span={24} sm={12} md={4}>
								<Button
									type="primary"
									htmlType="submit"
									size="large"
									style={{
										width: "100%",
									}}
									disabled={!acceptConditions || loading}
									loading={loading}
								>
									Submit Credit Form
								</Button>
							</Col>
						</Row>
					</Form>
				</Spin>
			)}
			{contextHolder}
		</Layout>
	);
};

export default CreditNew4;
